import React, { useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import {
  DiffStateMap,
  RESTORE_DIFFS_EXPAND_STATE,
  TOGGLE_DIFF_EXPANSION,
} from 'src/redux/pull-request/actions';
import {
  getAllDiffFiles,
  getCurrentPullRequestUrlPieces,
} from 'src/redux/pull-request/selectors';
import storage from 'src/utils/store';

import { ReviewedFilesContainer } from './reviewed-files-provider';

type KeyBuilder = (owner: string, slug: string, id: number | string) => string;

const createCacheKeyBuilder = (suffix: string): KeyBuilder => {
  return (owner, slug, id) => `${owner}/${slug}/${id}:${suffix}`;
};

export const buildLocalStorageKey = (
  owner: string,
  slug: string,
  id: string | number
) => {
  const DIFFS_REVIEWED_PERSISTENCE_KEY = 'files-reviewed-state';
  const buildDiffsReviewedKey = createCacheKeyBuilder(
    DIFFS_REVIEWED_PERSISTENCE_KEY
  );
  const diffsReviewedKey = buildDiffsReviewedKey(owner, slug, id);
  return diffsReviewedKey;
};

export const ReviewedFilesWrapper = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const dispatch = useDispatch();
  const allDiffFiles = useSelector(getAllDiffFiles);
  const { owner, slug, id = 0 } = useSelector(getCurrentPullRequestUrlPieces);
  const diffsReviewedKey = buildLocalStorageKey(owner, slug, id);

  const expandFiles = (filepaths: string[]) => {
    filepaths.forEach(filepath => {
      dispatch({
        type: TOGGLE_DIFF_EXPANSION,
        payload: { filepath, isOpening: true },
      });
    });
  };

  // Set initial expansion state of the files based on reviewed state
  useEffect(() => {
    const diffsExpansionState: DiffStateMap = {};
    const diffsViewedState = storage.get(diffsReviewedKey) || {};

    for (const k in diffsViewedState) {
      if (diffsViewedState[k]) {
        diffsExpansionState[k] = false;
      }
    }

    dispatch({
      type: RESTORE_DIFFS_EXPAND_STATE,
      payload: diffsExpansionState,
    });
  }, [diffsReviewedKey, dispatch]);

  return (
    <ReviewedFilesContainer
      diffsReviewedKey={diffsReviewedKey}
      allDiffFiles={allDiffFiles}
      handleUpdatedFiles={expandFiles}
    >
      {children}
    </ReviewedFilesContainer>
  );
};
